import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, List, ListItem, ListItemSecondaryAction, Typography } from "@material-ui/core";
import { TextField } from "../../TextField/TextField.styled";
import { BriefingFormModel, MAIN_ASSETS_LENGTH } from "pages/BriefingForm/interfaces";
import { Controller, useFieldArray } from "react-hook-form";
import { FramesSubSection } from "./FramesSubSection/FramesSubSection";
import { Dropzone } from "components";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";

interface DigitalBriefSectionProps extends FieldsHookForm<BriefingFormModel> {
    onUploadAsset(acceptedFiles: File[], index: number): void;
    onDeleteBriefFormAsset(id: number, removeFromArray?: boolean): void;
    onAdditionalAssetsUpload(files: File[]): void;
}

export const DigitalBriefSection: React.FC<DigitalBriefSectionProps> = ({
    control,
    errors,
    onDeleteBriefFormAsset,
    onUploadAsset,
    onAdditionalAssetsUpload,
}) => {
    const { t } = useTranslation();
    const { fields } = useFieldArray({ control, name: "digitalBrief.assets" });

    return (
        <Box>
            <Box mb={4}>
                <Controller
                    fullWidth
                    label={t("briefingForm.label.ioNumber")}
                    error={!!errors.digitalBrief?.ioNumber}
                    as={<TextField />}
                    control={control}
                    name="digitalBrief.ioNumber"
                    disabled
                />
                {errors.digitalBrief?.ioNumber && (
                    <Typography variant="body1" color="error">
                        {errors.digitalBrief?.ioNumber?.message}
                    </Typography>
                )}
            </Box>

            <Box mb={4}>
                <FramesSubSection
                    control={control}
                    onDeleteBriefFormAsset={onDeleteBriefFormAsset}
                    errors={errors}
                    onUploadAsset={onUploadAsset}
                    fields={fields}
                />
            </Box>

            <Box mb={4}>
                <Box mb={1}>
                    <Typography variant="subtitle1">{t("briefingForm.label.additionalAssets")}</Typography>
                </Box>

                {fields.length > MAIN_ASSETS_LENGTH && (
                    <Box maxHeight="240px" overflow="hidden" mb="24px" style={{ overflowY: "auto" }}>
                        <List>
                            {fields.slice(MAIN_ASSETS_LENGTH).map((asset, index) => (
                                <ListItem key={asset.id}>
                                    <Box mr="24px">
                                        <Controller
                                            as={<TextField />}
                                            name={`digitalBrief.assets[${index + MAIN_ASSETS_LENGTH}].id`}
                                            control={control}
                                            label={t("briefingForm.label.frameId")}
                                            disabled
                                        />
                                    </Box>
                                    <Controller
                                        as={<TextField />}
                                        name={`digitalBrief.assets[${index + MAIN_ASSETS_LENGTH}].fileName`}
                                        control={control}
                                        label={t("briefingForm.label.fileName")}
                                        disabled
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            onClick={() => onDeleteBriefFormAsset(parseInt(asset.id as string), true)}>
                                            <DeleteOutlinedIcon color="error" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                <Dropzone
                    sizeY={100}
                    showDimensions={false}
                    label={t("briefingForm.label.dropTextAdditionalAssets")}
                    onDrop={(files) => onAdditionalAssetsUpload(files)}
                />
            </Box>

            <Box mb={4}>
                <Box mb={1}>
                    <Typography variant="subtitle1">{t("briefingForm.label.callToActionDetailed")}</Typography>
                </Box>
                <Controller
                    fullWidth
                    label={t("briefingForm.label.callToAction")}
                    multiline
                    rows={5}
                    variant="outlined"
                    as={<TextField />}
                    control={control}
                    error={!!errors.digitalBrief?.callToAction}
                    name="digitalBrief.callToAction"
                />
                {errors.digitalBrief?.callToAction && (
                    <Typography variant="body1" color="error">
                        {errors.digitalBrief?.callToAction?.message}
                    </Typography>
                )}
            </Box>

            <Box mb={4}>
                <Typography variant="subtitle1">{t("briefingForm.label.landingUrlDetailed")}</Typography>
                <Controller
                    fullWidth
                    label={t("briefingForm.label.landingUrl")}
                    error={!!errors.digitalBrief?.landingURL}
                    as={<TextField />}
                    control={control}
                    name="digitalBrief.landingURL"
                />
                {errors.digitalBrief?.landingURL && (
                    <Typography variant="body1" color="error">
                        {errors.digitalBrief?.landingURL?.message}
                    </Typography>
                )}
            </Box>

            <Box>
                <Box mb={1}>
                    <Typography variant="subtitle1">{t("briefingForm.label.specialInstructions")}</Typography>
                    <Typography variant="subtitle2">{t("briefingForm.label.specialInstructionsDetailed")}</Typography>
                </Box>
                <Controller
                    fullWidth
                    label="Special Instructions"
                    multiline
                    rows={5}
                    variant="outlined"
                    as={<TextField />}
                    control={control}
                    name="digitalBrief.specialInstructions"
                />
            </Box>
        </Box>
    );
};
